import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { APP_CONSTANTS } from '../../app.constants';

export interface Favorite {
    id?: number;
    routeId: string;
    url: string;
}

interface PublicFavoritesResponse {
    ownerUserId: number;
    viewerUserId: number | null;
    viewerIsOwner: boolean;
    favorites: Favorite[];
}

@Injectable({ providedIn: 'root' })
export class FavoritesService {
    // Shared API URL prefix sourced from APP_CONSTANTS so the "/api" mount
    // point is not hard-coded throughout this service.
    private readonly API_PREFIX = APP_CONSTANTS.API_PREFIX;

    private currentUserId: number | null = null;
    private userFavorites$ = new BehaviorSubject<Favorite[]>([]);
    private publicFavorites$ = new BehaviorSubject<Favorite[]>([]);
    private publicFavoritesRequestToken = 0;

    constructor(
        private http: HttpClient,
        private toasts: ToastrService
    ) {}

    getUserFavorites(): Observable<Favorite[]> {
        return this.userFavorites$.asObservable();
    }

    getPublicFavorites(): Observable<Favorite[]> {
        return this.publicFavorites$.asObservable();
    }

    resetUserFavorites(): void {
        this.currentUserId = null;
        this.userFavorites$.next([]);
    }

    resetPublicFavorites(): void {
        this.publicFavoritesRequestToken++;
        this.publicFavorites$.next([]);
    }

    async loadUserFavorites(userId: number): Promise<void> {
        try {
            this.currentUserId = userId;
            const favorites = await firstValueFrom(
                this.http.get<{data: Favorite[]}>(`${this.API_PREFIX}/users/${userId}/user-favorites`).pipe(
                    map(({data}) => data)
                )
            );
            this.userFavorites$.next(favorites);
        } catch(_err) {
            console.error('unable to load user favorites');
        }
    }

    async addFavorite(favorite: Favorite): Promise<void> {
        try {
            await firstValueFrom(this.http.post(`${this.API_PREFIX}/users/${this.currentUserId}/user-favorite`, favorite));
            const newFavorites = [...this.userFavorites$.value, favorite];
            this.userFavorites$.next(newFavorites);
        } catch(_err) {
            this.toasts.error('common.toasts.errorOccurred');
            console.error('unable to add favorite');
        }

    }

    async loadPublicDashboardFavorites(publicDashboardId: string): Promise<PublicFavoritesResponse> {
        const requestToken = ++this.publicFavoritesRequestToken;
        const response = await firstValueFrom(
            this.http.get<{ data: PublicFavoritesResponse }>(`${this.API_PREFIX}/public-dashboard/${publicDashboardId}/user-favorites`).pipe(
                map(({ data }) => data)
            )
        );

        // Ignore stale responses so navigating between public dashboards cannot
        // repopulate the shared stream with favorites from an older request.
        if (requestToken === this.publicFavoritesRequestToken) {
            // Keep public dashboard data out of the signed-in user's favorites state.
            this.publicFavorites$.next(response.favorites);
        }

        return response;
    }

    async addPublicDashboardFavorite(publicDashboardId: string, favorite: Favorite): Promise<void> {
        try {
            await firstValueFrom(this.http.post(`${this.API_PREFIX}/public-dashboard/${publicDashboardId}/user-favorite`, favorite));
            const newFavorites = [...this.publicFavorites$.value, favorite];
            this.publicFavorites$.next(newFavorites);
        } catch(_err) {
            this.toasts.error('common.toasts.errorOccurred');
            console.error('unable to add public dashboard favorite');
        }
    }

    async removePublicDashboardFavorite(publicDashboardId: string, favorite: Favorite): Promise<void> {
        try {
            await firstValueFrom(
                this.http.delete(`${this.API_PREFIX}/public-dashboard/${publicDashboardId}/user-favorite/${favorite.routeId}`)
            );
            const favorites = this.publicFavorites$.value;
            const newFavorites = favorites.filter((f) => f.routeId !== favorite.routeId);
            this.publicFavorites$.next(newFavorites);
        } catch(_err) {
            this.toasts.error('common.toasts.errorOccurred');
            console.error('unable to remove public dashboard favorite');
        }
    }

    async removeFavorite(favorite: Favorite): Promise<void> {
        try {
            await firstValueFrom(this.http.delete(`${this.API_PREFIX}/users/${this.currentUserId}/user-favorite/${favorite.routeId}`));
            const favorites = this.userFavorites$.value;
            const newFavorites = favorites.filter(f => f.routeId !== favorite.routeId);
            this.userFavorites$.next(newFavorites);
        } catch(_err) {
            this.toasts.error('common.toasts.errorOccurred');
            console.error('unable to remove favorite');
        }
    }
}
